import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TransferCreateInput } from '@monigo/shared';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/queryKeys.js';

export interface TransferRow {
  id: string;
  reference_code: string;
  beneficiary_id: string;
  beneficiary_name?: string | null;
  beneficiary_payout_method?: string | null;
  beneficiary_payout_details?: Record<string, unknown> | null;
  from_currency: string;
  to_currency: string;
  amount_send: number;
  amount_receive: number;
  exchange_rate: number;
  fee: number;
  status: string;
  created_at: string;
  updated_at: string;
  payment_instructions_jsonb?: { settings_key?: string };
  promocode_discount_applied?: number;
  promo_code_id?: string | null;
}

export interface TransferEvent {
  id: string;
  from_status: string | null;
  to_status: string;
  actor_type: string;
  note: string | null;
  created_at: string;
}

export function useTransfers(filters?: { status?: string; page?: number; per_page?: number }) {
  return useQuery({
    queryKey: qk.transfers(filters),
    queryFn: () =>
      callFunction<{ items: TransferRow[]; total: number }>('transfers-list', {
        query: filters as Record<string, string | number | undefined>,
      }),
  });
}

export function useTransfer(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.transfer(id) : ['transfer', 'noop'],
    queryFn: () =>
      callFunction<{ transfer: TransferRow; events: TransferEvent[] }>('transfers-get', {
        query: { id: id! },
      }),
    enabled: !!id,
  });
}

export function useCreateTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TransferCreateInput) =>
      callFunction<{ transfer: TransferRow }>('transfers-create', { body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfers'] }),
  });
}

export function useCancelTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => callFunction('transfers-cancel', { body: { transfer_id: id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfers'] }),
  });
}

export interface QuoteResponse {
  rate: number;
  fee: number;
  amount_send: number;
  amount_receive: number;
  has_dynamic_tiers?: boolean;
}

export function useValidatePromoCode() {
  return useMutation({
    mutationFn: (input: { code: string; from_currency: string; amount_send: number; fee: number }) =>
      callFunction<{
        valid: boolean;
        discount_type: string | null;
        discount_value: number | null;
        name: string | null;
        promo_code_id: string | null;
        discount_applied: number | null;
      }>('promo-codes-validate', { body: input }),
  });
}

export function useQuote() {
  return useMutation({
    mutationFn: (input: { from_currency: string; to_currency: string; amount: number }) =>
      callFunction<QuoteResponse>('rates-quote', { body: input }),
  });
}
